import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import StatCardSkeleton from "pages/analytics/components/StatCardSkeleton";
import { ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import {
  formatNumber,
  formatPercent,
} from "pages/analytics/components/analyticsFormatters";
import { getGa4Metrics } from "services/ga4-analytics.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const KPI_CONFIG = [
  { key: "activeUsers", title: "Active Users", icon: "Users" },
  { key: "sessions", title: "Sessions", icon: "Activity" },
  { key: "newUsers", title: "New Users", icon: "UserPlus" },
  { key: "screenPageViews", title: "Page Views", icon: "Eye" },
  { key: "bounceRate", title: "Bounce Rate", icon: "TrendingDown", percent: true },
  { key: "engagementRate", title: "Engagement Rate", icon: "Zap", percent: true },
];

const EMPTY_TOTALS = {
  activeUsers: 0,
  sessions: 0,
  newUsers: 0,
  screenPageViews: 0,
  bounceRate: null,
  engagementRate: null,
};

export default function Ga4KpiCards({ api, filters, refreshKey }) {
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setTotals(EMPTY_TOTALS);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getGa4Metrics(api, { filters });
        if (!cancelled) setTotals(result?.totals ?? EMPTY_TOTALS);
      } catch {
        if (!cancelled) {
          setTotals(EMPTY_TOTALS);
          setError("Failed to load GA4 metrics. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    filters.dateRangeType,
    filters.customDateRange?.[0],
    filters.customDateRange?.[1],
    refreshKey,
  ]);

  if (isCustomRangeIncomplete(filters) && !isLoading) {
    return (
      <p className="text-sm text-gray-500 italic mb-6">
        Select start and end dates to view KPIs.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {KPI_CONFIG.map((kpi) => (
          <StatCardSkeleton key={kpi.key} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6">
        <ErrorBanner message={error} />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      {KPI_CONFIG.map(({ key, title, icon, percent }) => (
        <StatCard
          key={key}
          title={title}
          value={
            percent
              ? formatPercent(totals[key])
              : formatNumber(totals[key])
          }
          icon={icon}
        />
      ))}
    </div>
  );
}
